import { ToastProvider, UiLabelsProvider } from '@nexo/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  createHashRouter,
  type RouteObject,
} from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { lazy, Suspense, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './features/auth/AuthProvider';
import { AccountLanguageSync } from './lib/accountLanguage';
import { AccountTimeZoneSync } from './lib/accountTimeZone';
import { intlLocale } from './lib/i18n';
import { PresenceHeartbeat } from './lib/presence';
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage';
import { LoginPage } from './features/auth/LoginPage';
import { ResetPasswordPage } from './features/auth/ResetPasswordPage';
import { RegisterPage } from './features/auth/RegisterPage';
import { RedirectIfAuthenticated, RequireAuth } from './features/auth/RequireAuth';
import { ChannelView } from './features/chat/ChannelView';
import { LandingPage } from './features/landing/LandingPage';
import { ProfilePage } from './features/profile/ProfilePage';
import { NewsPage } from './features/news/NewsPage';
import { WhatsNewDialog } from './features/news/WhatsNewDialog';
import { SavedPage } from './features/chat/SavedPage';
import { GuestMeetingPage } from './features/meetings/GuestMeetingPage';
import { MeetingsPage } from './features/meetings/MeetingsPage';
import { TasksPage } from './features/tasks/TasksPage';
import { SprintsPage } from './features/sprints/SprintsPage';
import { AppIndex, HomePage } from './features/teams/HomePage';
import { InvitePage } from './features/teams/InvitePage';
import { JoinPage } from './features/teams/JoinPage';
import { HalloweenBats } from './components/HalloweenBats';
import { NotFound } from './components/NotFound';
import { AdminAudit } from './features/admin/AdminAudit';
import { AdminFeedback } from './features/admin/AdminFeedback';
import { AdminLayout } from './features/admin/AdminLayout';
import { AdminOverview } from './features/admin/AdminOverview';
import { AdminReleases } from './features/admin/AdminReleases';
import { AdminSystem } from './features/admin/AdminSystem';
import { AdminTeams } from './features/admin/AdminTeams';
import { AdminTeamView } from './features/admin/AdminTeamView';
import { AdminUsers } from './features/admin/AdminUsers';
import { DownloadPage } from './features/download/DownloadPage';

const CallPage = lazy(() => import('./features/calls/CallPage'));
import { RouteError } from './components/RouteError';
import { LegalPage } from './features/legal/LegalPage';
import { CalendarPage } from './features/calendar/CalendarPage';
import { ExplorePage } from './features/teams/ExplorePage';
import { AdminFeatures } from './features/admin/AdminFeatures';
import { useTeam } from './layouts/TeamContext';
import { TeamShell } from './layouts/TeamShell';
import { isDesktop } from './lib/platform';
import { RealtimeProvider } from './realtime/RealtimeProvider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, error) =>
        count < 2 && !(error instanceof Error && 'status' in error && Number(error.status) < 500),
      refetchOnWindowFocus: true,
    },
  },
});

/** Providers that need the router (navigation) live inside it. */
function Root() {
  return (
    <ToastProvider>
      <AuthProvider>
        <AccountLanguageSync />
        <AccountTimeZoneSync />
        <PresenceHeartbeat />
        <Outlet />
      </AuthProvider>
    </ToastProvider>
  );
}

function AuthenticatedApp() {
  return (
    <RequireAuth>
      <RealtimeProvider>
        <Outlet />
        <WhatsNewDialog />
      </RealtimeProvider>
    </RequireAuth>
  );
}

function TeamIndex() {
  const { team, channels } = useTeam();
  const general =
    channels.find((c) => c.kind === 'channel' && c.name === 'general') ??
    channels.find((c) => c.mode === 'text') ??
    channels[0];
  return (
    <Navigate
      to={general ? `/app/t/${team.id}/c/${general.id}` : `/app/t/${team.id}/tasks`}
      replace
    />
  );
}

const routes: RouteObject[] = [
  {
    element: <Root />,
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <LandingPage /> },
      {
        path: '/login',
        element: (
          <RedirectIfAuthenticated>
            <LoginPage />
          </RedirectIfAuthenticated>
        ),
      },
      { path: '/download', element: <DownloadPage /> },
      { path: '/privacy', element: <LegalPage document="privacy" /> },
      { path: '/terms', element: <LegalPage document="terms" /> },
      { path: '/legal', element: <LegalPage document="notice" /> },
      { path: '/m/:token', element: <GuestMeetingPage /> },
      {
        path: '/call',
        element: (
          <Suspense fallback={null}>
            <CallPage />
          </Suspense>
        ),
      },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      {
        path: '/register',
        element: (
          <RedirectIfAuthenticated>
            <RegisterPage />
          </RedirectIfAuthenticated>
        ),
      },
      {
        element: <AuthenticatedApp />,
        children: [
          { path: '/invite/:token', element: <InvitePage /> },
          { path: '/join/:token', element: <JoinPage /> },
          { path: '/app', element: <AppIndex /> },
          { path: '/app/home', element: <HomePage /> },
          { path: '/app/explore', element: <ExplorePage /> },
          { path: '/app/profile', element: <ProfilePage /> },
          { path: '/app/news', element: <NewsPage /> },
          {
            path: '/admin',
            element: <AdminLayout />,
            children: [
              { index: true, element: <AdminOverview /> },
              { path: 'users', element: <AdminUsers /> },
              { path: 'teams', element: <AdminTeams /> },
              { path: 'teams/:teamId', element: <AdminTeamView /> },
              { path: 'feedback', element: <AdminFeedback /> },
              { path: 'releases', element: <AdminReleases /> },
              { path: 'features', element: <AdminFeatures /> },
              { path: 'audit', element: <AdminAudit /> },
              { path: 'system', element: <AdminSystem /> },
            ],
          },
          {
            path: '/app/t/:teamId',
            element: <TeamShell />,
            children: [
              { index: true, element: <TeamIndex /> },
              { path: 'c/:channelId', element: <ChannelView /> },
              { path: 'tasks', element: <TasksPage /> },
              { path: 'sprints', element: <SprintsPage /> },
              { path: 'saved', element: <SavedPage /> },
              { path: 'meetings', element: <MeetingsPage /> },
              { path: 'calendar', element: <CalendarPage /> },
            ],
          },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
];

// The desktop webview serves static files, so it uses hash URLs (no server-side fallback needed).
const router = isDesktop ? createHashRouter(routes) : createBrowserRouter(routes);

/** Translates the built-in texts of the @nexo/ui components (the package doesn't use i18n). */
function TranslatedUi({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <UiLabelsProvider
      labels={{
        close: t('Cerrar'),
        loading: t('Cargando'),
        online: t('En línea'),
        offline: t('Desconectado'),
        choose: t('Elige…'),
        today: t('Hoy'),
        clear: t('Quitar'),
        previousMonth: t('Mes anterior'),
        nextMonth: t('Mes siguiente'),
        noDate: t('Sin fecha'),
        locale: intlLocale(),
      }}
    >
      {children}
    </UiLabelsProvider>
  );
}

/**
 * Remounts the pages when the language changes: some texts (task groups, dates, labels built by
 * helpers) are computed outside components that subscribe to translations.
 */
function LocalizedRouter() {
  const { i18n } = useTranslation();
  return <RouterProvider key={i18n.language} router={router} />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TranslatedUi>
        <LocalizedRouter />
      </TranslatedUi>
      <HalloweenBats />
    </QueryClientProvider>
  );
}
